import React, { useState } from 'react';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { X } from 'lucide-react';

const popularInterests = [
  'Travel', 'Photography', 'Hiking', 'Yoga', 'Cooking', 'Wine Tasting',
  'Scuba Diving', 'Art', 'Music', 'Dancing', 'Reading', 'Fitness',
  'Surfing', 'Skiing', 'Beach', 'Mountains', 'Food', 'Culture',
  'History', 'Architecture', 'Nature', 'Wildlife', 'Adventure Sports',
  'Meditation', 'Wellness', 'Fashion', 'Nightlife', 'Museums'
];

const commonLanguages = [
  'English', 'Spanish', 'French', 'German', 'Italian', 'Portuguese',
  'Mandarin', 'Hindi', 'Arabic', 'Japanese', 'Korean', 'Russian',
  'Turkish', 'Greek', 'Dutch', 'Swedish'
];

const InterestsStep = ({ formData, updateFormData }) => {
  const [customInterest, setCustomInterest] = useState('');
  const [customLanguage, setCustomLanguage] = useState('');
  
  const toggleInterest = (interest) => {
    const current = formData.interests || [];
    if (current.includes(interest)) {
      updateFormData({
        interests: current.filter(i => i !== interest)
      });
    } else {
      updateFormData({
        interests: [...current, interest]
      });
    }
  };
  
  const addCustomInterest = () => {
    if (customInterest.trim()) {
      const current = formData.interests || [];
      if (!current.includes(customInterest.trim())) {
        updateFormData({
          interests: [...current, customInterest.trim()]
        });
      }
      setCustomInterest('');
    }
  };
  
  const toggleLanguage = (language) => {
    const current = formData.languages || [];
    if (current.includes(language)) {
      updateFormData({
        languages: current.filter(l => l !== language)
      });
    } else {
      updateFormData({
        languages: [...current, language]
      });
    }
  };
  
  const addCustomLanguage = () => {
    if (customLanguage.trim()) {
      const current = formData.languages || [];
      if (!current.includes(customLanguage.trim())) {
        updateFormData({
          languages: [...current, customLanguage.trim()]
        });
      }
      setCustomLanguage('');
    }
  };
  
  return (
    <div className="space-y-6">
      <p className="text-neutral-600 mb-6">
        Tell us about your interests and write a bit about yourself
      </p>
      
      {/* Interests */}
      <div className="space-y-3">
        <Label>Your Interests *</Label>
        <p className="text-sm text-neutral-500">Select at least 3 interests</p>
        <div className="flex flex-wrap gap-2">
          {popularInterests.map((interest) => (
            <Badge
              key={interest}
              variant={formData.interests?.includes(interest) ? 'default' : 'outline'}
              className="cursor-pointer px-3 py-1.5 text-sm"
              onClick={() => toggleInterest(interest)}
            >
              {interest}
            </Badge>
          ))}
        </div>
        
        {/* Custom Interest */}
        <div className="flex gap-2">
          <Input
            placeholder="Add custom interest"
            value={customInterest}
            onChange={(e) => setCustomInterest(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addCustomInterest())}
          />
          <button
            onClick={addCustomInterest}
            className="px-4 py-2 bg-primary-700 text-white rounded-lg hover:bg-primary-600 transition-all"
          >
            Add
          </button>
        </div>
      </div>
      
      {/* Languages */}
      <div className="space-y-3">
        <Label>Languages You Speak *</Label>
        <p className="text-sm text-neutral-500">Select all languages you're comfortable with</p>
        <div className="flex flex-wrap gap-2">
          {commonLanguages.map((language) => (
            <Badge
              key={language}
              variant={formData.languages?.includes(language) ? 'default' : 'outline'}
              className="cursor-pointer px-3 py-1.5 text-sm"
              onClick={() => toggleLanguage(language)}
            >
              {language}
            </Badge>
          ))}
        </div>
        
        {/* Custom Language */}
        <div className="flex gap-2">
          <Input
            placeholder="Add another language"
            value={customLanguage}
            onChange={(e) => setCustomLanguage(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addCustomLanguage())}
          />
          <button
            onClick={addCustomLanguage}
            className="px-4 py-2 bg-primary-700 text-white rounded-lg hover:bg-primary-600 transition-all"
          >
            Add
          </button>
        </div>
      </div>
      
      {/* Bio */}
      <div className="space-y-3">
        <Label htmlFor="bio">About You *</Label>
        <p className="text-sm text-neutral-500">
          Tell others about yourself so we can help you connect
        </p>
        <Textarea
          id="bio"
          placeholder="Share a bit about yourself, your travel experiences, what you're passionate about, and what makes you unique..."
          value={formData.bio}
          onChange={(e) => updateFormData({ bio: e.target.value })}
          rows={6}
          maxLength={500}
        />
        <p className="text-xs text-neutral-500 text-right">
          {formData.bio?.length || 0} / 500 characters
        </p>
      </div>
      
      {/* Tips */}
      <div className="bg-secondary-50 border border-secondary-200 rounded-lg p-4">
        <h4 className="font-semibold text-primary-900 mb-2">Make your profile shine ✨</h4>
        <ul className="text-sm text-neutral-700 space-y-1">
          <li>• Be authentic and genuine</li>
          <li>• Share your travel dreams and experiences</li>
          <li>• Highlight what makes you unique</li>
          <li>• Keep it positive and engaging</li>
        </ul>
      </div>
    </div>
  );
};

export default InterestsStep;
